// components/EvaluationDetailModal.jsx
import React from 'react';
import { Modal, Button } from 'react-bootstrap';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg:     '#08101E',
  bg1:    '#0C1525',
  bg2:    '#101B2E',
  bg3:    '#141F33',
  border: '#1E2D42',
  text:   '#E8EDF5',
  body:   '#B8C8DC',
  meta:   '#6A7D95',
  green:  '#00E896',
  cyan:   '#00C8FF',
  amber:  '#FFB340',
  red:    '#FF4466',
  blue:   '#4488FF',
  purple: '#9966FF',
  font:   "'DM Mono', monospace",
};

// ─── Decision → color (covers all variants) ──────────────────────────────────
const decisionColor = (d) => {
  const s = String(d || '').toUpperCase();
  if (s === 'PROCEED' || s === 'BUY')            return C.green;
  if (s === 'SKIP'    || s === 'SKIP_TRADE')     return C.meta;
  if (s === 'HOLD')                              return C.cyan;
  if (s === 'ADJUST')                            return C.amber;
  if (s === 'CLOSE')                             return C.red;
  return C.meta;
};

// ─── Pill (replaces Bootstrap Badge) ─────────────────────────────────────────
const Pill = ({ children, color = C.meta, size = 10, style = {} }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 8px', fontSize: size, fontFamily: C.font,
    fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
    color, background: color + '20', border: `1px solid ${color}50`,
    borderRadius: 4, lineHeight: '16px', whiteSpace: 'nowrap',
    ...style,
  }}>
    {children}
  </span>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtPct = (v, dp = 0) =>
  (typeof v === 'number' ? `${(v * 100).toFixed(dp)}%` : '—');

const fmtNum = (v, dp = 2) =>
  (typeof v === 'number' && !Number.isNaN(v) ? Number(v).toFixed(dp) : '—');

const fmtTime = (ts) => {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  } catch {
    return '—';
  }
};

// ─── Layout primitives ────────────────────────────────────────────────────────
const Section = ({ title, children, pad = true }) => (
  <div style={{ marginBottom: 12, borderRadius: 6, backgroundColor: C.bg1, border: `1px solid ${C.border}` }}>
    <div style={{ padding: '10px 14px 4px' }}>
      <div style={{ color: C.meta, letterSpacing: '0.08em', fontFamily: C.font, fontSize: 9, textTransform: 'uppercase', marginBottom: 6 }}>
        {title}
      </div>
    </div>
    <div style={pad ? { padding: '0 14px 12px' } : {}}>
      {children}
    </div>
  </div>
);

const Divider = () => <div style={{ height: 1, background: C.border, margin: '8px 0' }} />;

// Grid row of label+value pairs
const KVGrid = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px 16px' }}>
    {children}
  </div>
);

const KeyVal = ({ label, value, color = C.text }) => (
  <div>
    <div style={{ fontSize: 9, color: C.meta, fontFamily: C.font, letterSpacing: '0.06em', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 11, color, fontFamily: C.font, fontWeight: 500 }}>{value ?? '—'}</div>
  </div>
);

// Bootstrap grid shim — keeps existing className="col-..." children working
const KeyValBootstrap = ({ label, value, strong = true }) => (
  <div className="col-12 col-sm-6 col-md-3">
    <small className="d-block" style={{ color: C.meta }}>{label}</small>
    {strong
      ? <strong style={{ color: C.text }}>{value}</strong>
      : <span    style={{ color: C.text }}>{value}</span>}
  </div>
);

const isNonEmptyObject = (o) => o && typeof o === 'object' && Object.keys(o).length > 0;

const JsonBlock = ({ data }) => (
  <pre style={{
    margin: 0, padding: 8, borderRadius: 4,
    backgroundColor: C.bg2, border: `1px solid ${C.border}`,
    color: C.body, whiteSpace: 'pre-wrap', fontSize: 10, fontFamily: C.font,
  }}>
    {JSON.stringify(data, null, 2)}
  </pre>
);

const OptionalSection = ({ title, value }) => {
  if (value == null) return null;
  const t = typeof value;
  if (t === 'string' && value.trim() === '') return null;
  return (
    <Section title={title}>
      {t === 'string' ? (
        <div style={{ padding: 8, borderRadius: 4, background: C.bg2, border: `1px solid ${C.border}`, whiteSpace: 'pre-wrap', fontSize: 11, color: C.body, fontFamily: C.font }}>
          {value}
        </div>
      ) : (
        <JsonBlock data={value} />
      )}
    </Section>
  );
};

// ─── Regime context extractor (BRD-2) ────────────────────────────────────────
const extractRegimeCtx = (agentStory) => {
  if (!agentStory) return null;
  const msnap = agentStory.market_snapshot || {};
  const rc    = msnap.regime_context || {};
  const rs    = agentStory.regime_summary || {};
  if (!Object.keys(rc).length && !Object.keys(rs).length) return null;
  return {
    dayType:              rs.day_type              || rc.day_type              || null,
    dayTypeReason:        rc.day_type_reason       || null,
    entryType:            rs.entry_type            || rc.entry_type            || null,
    entryAllowed:         rc.entry_allowed         ?? rs.entry_allowed         ?? null,
    entryBlockReason:     rc.entry_block_reason    || null,
    earlyEntryWindowOpen: rc.early_entry_window_open ?? null,
    priorProceedTaken:    rc.prior_proceed_taken   ?? false,
    eventDay:             rc.event_day_detected    ?? false,
    der:                  rc.der                   ?? null,
    ci14:                 rc.ci_14                 ?? null,
    ci6:                  rc.ci_6                  ?? null,
    volatilityContext:    rs.volatility_context    || rc.volatility_context    || null,
    volatilityReason:     rc.volatility_context_reason || null,
    atr5:                 rc.atr_5                 ?? null,
    atr14:                rc.atr_14                ?? null,
    rrFloor:              rs.rr_floor              ?? rc.rr_floor              ?? null,
    rrPreview:            msnap.rr_preview         ?? null,
    scaleCap:             rs.scale_cap             ?? rc.scale_cap             ?? null,
    stopFloor:            rs.stop_floor_pct        ?? rc.stop_floor_pct        ?? null,
    cooldownActive:       msnap.cooldown_active    ?? false,
    cooldownThreshold:    rc.cooldown_threshold    ?? null,
    cooldownImpulse:      msnap.cooldown_impulse_atr_5bar ?? null,
    cooldownReason:       msnap.cooldown_reason    || null,
    stabilityScore:       msnap.stability_score    ?? null,
    gapPct:               rc.gap_pct               ?? null,
    dayRangePct:          rc.day_range_pct         ?? null,
    pdcDisplacementPct:   rc.pdc_displacement_pct  ?? null,
  };
};

const dayTypeColor = (dt) => ({
  TREND_DAY: C.green, STRONG_TREND_DAY: C.green,
  EVENT_TREND: C.cyan, EVENT_DAY: C.cyan,
  WEAK_TREND_DAY: '#88D400',
  RANGE_DAY: C.blue, SIDEWAYS_DAY: C.purple,
  CHOPPY_DAY: C.red, VOLATILE_DAY: C.amber,
})[dt] || C.meta;

// === Chart LLM helpers & component ===
const buildChartLLM = (chartAnalysis) => {
  if (!chartAnalysis) return null;
  const llm = chartAnalysis.llm_full_response || {};
  const oa = llm.overall_assessment || {};
  const fft = llm.fft_lag_intelligence || {};
  const patterns = Array.isArray(llm.identified_patterns) ? llm.identified_patterns : [];

  return {
    dominant_direction: oa.dominant_direction ?? 'neutral',
    action_recommendation: oa.action_recommendation ?? 'hold_position',
    confidence_in_analysis: oa.confidence_in_analysis ?? null,
    pattern_confluence_strength: oa.pattern_confluence_strength ?? null,
    bullish_signal_strength: oa.bullish_signal_strength ?? null,
    bearish_signal_strength: oa.bearish_signal_strength ?? null,
    support: oa.key_support_level ?? null,
    resistance: oa.key_resistance_level ?? null,
    conflicting_signals_present: !!oa.conflicting_signals_present,
    patterns: patterns.slice(0, 3).map((p) => ({
      name: p.pattern_name,
      type: p.pattern_type,
      direction:
        p.interpretation?.toLowerCase().includes('bear') ? 'bearish' :
        p.interpretation?.toLowerCase().includes('bull') ? 'bullish' : 'neutral',
      confidence: p.confidence ?? 0,
      rec: p.recommended_action ?? null,
    })),
    timing_summary: fft.timing_relationships_summary ?? null,
    optimal_action_timing: fft.optimal_action_timing ?? null,
    lag_confidence_score: fft.lag_confidence_score ?? null,
    raw: llm,
    source_node: chartAnalysis?.analyzed_at_node ?? null,
  };
};

const ChartLLMAnalysis = ({ data }) => {
  if (!data) return null;
  const pct    = (v) => (v === 0 || v ? `${Math.round(v * 100)}%` : '—');
  const dirCol = (dir) => dir === 'bullish' ? C.green : dir === 'bearish' ? C.red : C.meta;

  return (
    <div style={{ padding: 12, borderRadius: 6, backgroundColor: C.bg2, border: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        <Pill color={dirCol(data.dominant_direction)}>Direction: {data.dominant_direction}</Pill>
        <Pill color={C.body}>Action: {data.action_recommendation}</Pill>
        {data.confidence_in_analysis != null && <Pill color={C.cyan}>Conf: {data.confidence_in_analysis.toFixed(2)}</Pill>}
        {data.pattern_confluence_strength != null && <Pill color={C.blue}>Confluence: {pct(data.pattern_confluence_strength)}</Pill>}
        {data.support    && <Pill color={C.green}>S: {data.support}</Pill>}
        {data.resistance && <Pill color={C.red}>R: {data.resistance}</Pill>}
        {data.conflicting_signals_present && <Pill color={C.amber}>Conflicting signals</Pill>}
      </div>

      {data.patterns?.length > 0 && (
        <>
          <div style={{ fontSize: 9, color: C.meta, marginBottom: 5, letterSpacing: '0.06em' }}>TOP PATTERNS</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
            {data.patterns.map((p, i) => (
              <Pill key={i} color={dirCol(p.direction)}>
                {p.name} · {p.type} · {pct(p.confidence)}
              </Pill>
            ))}
          </div>
        </>
      )}

      {(data.timing_summary || data.optimal_action_timing || data.lag_confidence_score != null) && (
        <div style={{ fontSize: 10, color: C.body, lineHeight: 1.7, marginBottom: 6 }}>
          {data.timing_summary         && <div>Timing: {data.timing_summary}</div>}
          {data.optimal_action_timing  && <div>Next trigger: {data.optimal_action_timing}</div>}
          {data.lag_confidence_score != null && <div>Lag confidence: {data.lag_confidence_score.toFixed(2)}</div>}
        </div>
      )}
      {data.source_node && (
        <div style={{ fontSize: 9, color: C.meta, marginTop: 4 }}>Analyzed at: {data.source_node}</div>
      )}

      <details style={{ marginTop: 8 }}>
        <summary style={{ cursor: 'pointer', color: C.meta, fontSize: 10 }}>Raw chart LLM JSON</summary>
        <div style={{ marginTop: 6 }}>
          <JsonBlock data={data.raw} />
        </div>
      </details>
    </div>
  );
};

const EvaluationDetailModal = ({ evaluation, show, onHide }) => {
  if (!evaluation) return null;

  // Normalize top-level fields (back-compat)
  const fullData = evaluation.fullData || evaluation || {};
  const agentStory =
    fullData.agent_story ||
    fullData.eval_agent_story ||
    evaluation.agent_story ||
    {};

  const chartLLM = buildChartLLM(agentStory.chart_analysis);

  // Shorthands for new schema
  const ids = agentStory.ids || {};
  const dc = agentStory.decision_context || {};
  const ds = agentStory.decision || {};
  const perf = agentStory.performance || {};
  const msum = agentStory.memory_summary || {};
  const ptag = agentStory.pattern_tag_analysis || {};
  const think = agentStory.thinking_snapshots || {};
  const rctl = agentStory.risk_controls || {};
  const contra = agentStory.contradictions || {};
  const ops = agentStory.ops || agentStory.stats || {};
  const narrative = fullData.narrative_summary || evaluation.narrative_summary;

  // Legacy fallbacks and extra fields
  const legacyReasoning = fullData.reasoning || evaluation.reasoning;
  const patternInfluence = fullData.pattern_influence;
  const executionTime = fullData.execution_time; // seconds
  const timeframe = agentStory.timeframe_protection || {};
  const genAIraw = agentStory; // raw llm content if you kept it

  // Legacy/experimental blocks (render if present)
  const exitRisk = fullData.exit_risk || agentStory.exit_risk;
  const redFlags = fullData.red_flags || agentStory.red_flags;
  const journey = fullData.journey || agentStory.journey;
  const signals = fullData.signals || agentStory.signals;

  const finalDecisionChip = ds.final || evaluation.decision || fullData.decision;

  const extractDecisionTime = (positionId) => {
    if (!positionId || typeof positionId !== 'string') return null;
    const parts = positionId.split('-');
    const last = parts[parts.length - 1];
    if (!/^\d{6}$/.test(last)) return null; // expect HHMMSS
    const hh = last.slice(0, 2);
    const mm = last.slice(2, 4);
    const ss = last.slice(4, 6);
    return `${hh}:${mm}:${ss}`;
  };

  // BRD-2 regime context
  const rc = extractRegimeCtx(agentStory);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      contentClassName="bg-dark text-light"
    >
      <div style={{ backgroundColor: C.bg, fontFamily: C.font }}>
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ backgroundColor: C.bg1, borderBottom: `1px solid ${C.border}` }}
        >
          <Modal.Title style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: C.font, fontSize: 13, color: C.text, fontWeight: 600 }}>
                AI Evaluation Details
              </span>
              {finalDecisionChip && (
                <Pill color={decisionColor(finalDecisionChip)} size={11}>
                  {String(finalDecisionChip).toUpperCase()}
                </Pill>
              )}
              {perf?.confidence_level && (
                <Pill color={C.cyan}>Conf: {perf.confidence_level}</Pill>
              )}
              {perf?.similarity_score !== undefined && (
                <Pill color={C.meta}>Sim: {fmtPct(perf.similarity_score)}</Pill>
              )}
              {typeof executionTime === 'number' && (
                <Pill color={C.meta}>Exec: {executionTime.toFixed(2)}s</Pill>
              )}
              {rc?.eventDay && <Pill color={C.cyan}>Event Day</Pill>}
              {rc?.cooldownActive && <Pill color={C.red}>Cooldown Active</Pill>}
            </div>
            <div style={{ fontSize: 10, color: C.meta, fontFamily: C.font, marginTop: 5 }}>
              {ids?.position_id ? `Position: ${ids.position_id}` : evaluation.position_id || ''}
              {agentStory?.schema_version ? ` · Schema ${agentStory.schema_version}` : ''}
              {agentStory.analysis_time ? ` · ${agentStory.analysis_time}` : null}
            </div>
            {agentStory?.ids?.cluster_id && (
              <div style={{ color: C.meta, fontFamily: C.font, fontSize: 9, marginTop: 2 }}>
                Cluster: {agentStory.ids.cluster_id}
              </div>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ background: C.bg, padding: 18, maxHeight: '75vh', overflowY: 'auto' }}>

          {/* ── BRD-2 Regime Context ── */}
          {rc && (
            <Section title="Session Context at Eval Time">
              {/* Alert: entry blocked */}
              {rc.entryAllowed === false && (
                <div style={{
                  background: C.amber + '15', border: `1px solid ${C.amber}50`,
                  borderRadius: 5, padding: '8px 12px', marginBottom: 10,
                  fontSize: 10, color: C.amber, fontFamily: C.font,
                }}>
                  Entry blocked{rc.entryBlockReason ? `: ${rc.entryBlockReason}` : ''}
                </div>
              )}
              {/* Alert: event day */}
              {rc.eventDay && (rc.gapPct != null || rc.dayRangePct != null) && (
                <div style={{
                  background: C.cyan + '12', border: `1px solid ${C.cyan}40`,
                  borderRadius: 5, padding: '8px 12px', marginBottom: 10,
                  fontSize: 10, color: C.cyan, fontFamily: C.font,
                }}>
                  Event day detected
                  {rc.gapPct      != null ? ` · Gap ${(rc.gapPct * 100).toFixed(2)}%` : ''}
                  {rc.dayRangePct != null ? ` · Day range ${(rc.dayRangePct * 100).toFixed(2)}%` : ''}
                  {rc.pdcDisplacementPct != null ? ` · PDC displacement ${(rc.pdcDisplacementPct * 100).toFixed(2)}%` : ''}
                </div>
              )}

              {/* Main grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px 16px', marginBottom: 10 }}>
                {rc.dayType && (
                  <KeyVal label="Day Type" value={
                    <Pill color={dayTypeColor(rc.dayType)}>{rc.dayType.replace(/_/g, ' ')}</Pill>
                  } />
                )}
                {rc.entryType && (
                  <KeyVal label="Entry Type" value={
                    <Pill color={rc.entryType === 'EARLY_ENTRY' ? C.amber : C.cyan}>
                      {rc.entryType.replace(/_/g, ' ')}
                    </Pill>
                  } />
                )}
                {rc.volatilityContext && (
                  <KeyVal label="Volatility"
                    value={rc.volatilityReason || rc.volatilityContext}
                    color={C.body}
                  />
                )}
                {rc.der != null && (
                  <KeyVal label="Trend Efficiency (DER)"
                    value={fmtNum(rc.der, 3)}
                    color={rc.der >= 0.7 ? C.green : rc.der >= 0.4 ? C.amber : C.red}
                  />
                )}
                {rc.ci14 != null && (
                  <KeyVal label="Chop Index 14 / 6"
                    value={`${fmtNum(rc.ci14, 1)} / ${rc.ci6 != null ? fmtNum(rc.ci6, 1) : '—'}`}
                    color={rc.ci14 < 38.2 ? C.green : rc.ci14 > 61.8 ? C.red : C.amber}
                  />
                )}
                {rc.atr5 != null  && <KeyVal label="ATR 5"  value={fmtNum(rc.atr5,  3)} />}
                {rc.atr14 != null && <KeyVal label="ATR 14" value={fmtNum(rc.atr14, 3)} />}
                {rc.stabilityScore != null && (
                  <KeyVal label="Stability Score" value={fmtNum(rc.stabilityScore, 2)}
                    color={rc.stabilityScore >= 0.7 ? C.green : rc.stabilityScore >= 0.4 ? C.amber : C.red}
                  />
                )}
              </div>

              <Divider />

              {/* Risk parameters row */}
              <div style={{ marginBottom: 6 }}>
                <div style={{ fontSize: 9, color: C.meta, letterSpacing: '0.06em', marginBottom: 5 }}>ACTIVE RISK PARAMS</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {rc.rrFloor   != null && <Pill color={C.cyan}>RR floor {fmtNum(rc.rrFloor, 2)}</Pill>}
                  {rc.rrPreview != null && (
                    <Pill color={rc.rrPreview >= (rc.rrFloor ?? 0) ? C.green : C.red}>
                      RR preview {fmtNum(rc.rrPreview, 2)}
                      {rc.rrFloor != null
                        ? rc.rrPreview >= rc.rrFloor ? ' · above floor' : ' · BELOW FLOOR'
                        : ''}
                    </Pill>
                  )}
                  {rc.scaleCap  != null && <Pill color={C.blue}>Scale cap {rc.scaleCap}x</Pill>}
                  {rc.stopFloor != null && <Pill color={C.amber}>Stop floor {fmtPct(rc.stopFloor)}</Pill>}
                  {rc.priorProceedTaken && <Pill color={C.amber}>Capital committed</Pill>}
                  {!rc.earlyEntryWindowOpen && rc.earlyEntryWindowOpen !== null && (
                    <Pill color={C.meta}>Entry window closed</Pill>
                  )}
                </div>
              </div>

              {/* Cooldown row */}
              {rc.cooldownActive && (
                <div style={{
                  marginTop: 8, padding: '6px 10px', borderRadius: 4,
                  background: C.red + '12', border: `1px solid ${C.red}40`,
                  fontSize: 10, color: C.red, fontFamily: C.font,
                }}>
                  Cooldown active
                  {rc.cooldownImpulse  != null ? ` · Impulse ${fmtNum(rc.cooldownImpulse, 2)} ATR` : ''}
                  {rc.cooldownThreshold != null ? ` · Threshold ${rc.cooldownThreshold}` : ''}
                  {rc.cooldownReason   ? ` · ${rc.cooldownReason}` : ''}
                </div>
              )}

              {/* Day type reason — if present */}
              {rc.dayTypeReason && (
                <div style={{ marginTop: 8, fontSize: 10, color: C.body, lineHeight: 1.6 }}>
                  {rc.dayTypeReason}
                </div>
              )}
            </Section>
          )}
          {/* Decision & Context */}
          {(ds.final || dc.symbol || dc.side || dc.price_at_decision !== undefined) && (
            <Section title="Decision & Context">
              <div className="row g-3">
                {ds.final && <KeyValBootstrap label="Final Decision" value={ds.final} />}
                {(typeof ds.confidence === 'number' || Array.isArray(ds.confidence_ci)) && (
                  <KeyValBootstrap
                    label="Confidence"
                    value={
                      <>
                        {typeof ds.confidence === 'number' ? fmtPct(ds.confidence) : '—'}
                        {Array.isArray(ds.confidence_ci) && ds.confidence_ci.length === 2 ? (
                          <span style={{ color: C.meta }}>
                            {' '}(CI {fmtPct(ds.confidence_ci[0])}–{fmtPct(ds.confidence_ci[1])})
                          </span>
                        ) : null}
                      </>
                    }
                  />
                )}

                {Array.isArray(agentStory?.decision?.rationale_lines) &&
                  agentStory.decision.rationale_lines.length > 0 && (
                    <ul className="small mt-2 mb-0">
                      {agentStory.decision.rationale_lines.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  )}

                {(ds.new_target !== undefined || ds.new_stop_loss !== undefined) && (
                  <KeyValBootstrap
                    label="Adjustments"
                    value={
                      <>
                        TP: {ds.new_target != null ? ds.new_target : '—'}{' '}
                        <span style={{ color: C.meta }}>·</span>{' '}
                        SL: {ds.new_stop_loss != null ? ds.new_stop_loss : '—'}
                      </>
                    }
                  />
                )}
                {(dc.symbol || dc.side || dc.price_at_decision !== undefined ||
                  dc.pnl_at_decision !== undefined || dc.timestamp) && (
                  <div className="col-12">
                    <div className="rounded p-3" style={{ backgroundColor: C.bg2, border: `1px solid ${C.border}` }}>
                      <div className="row g-3">
                        {dc.symbol && <KeyValBootstrap label="Symbol" value={dc.symbol} />}
                        {dc.side && <KeyValBootstrap label="Side" value={dc.side} />}
                        {dc.price_at_decision !== undefined && (
                          <KeyValBootstrap label="Price at Decision" value={`$${fmtNum(dc.price_at_decision, 2)}`} />
                        )}
                        {dc.pnl_at_decision !== undefined && (
                          <KeyValBootstrap label="P&L at Decision" value={fmtNum(dc.pnl_at_decision, 2)} />
                        )}
                        {dc.timestamp && <KeyValBootstrap label="Timestamp" value={fmtTime(dc.timestamp)} />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Reasoning */}
          <Section title="Reasoning">
            <div
              className="p-3 rounded"
              style={{ backgroundColor: '#101B2E', border: '1px solid #1E2D42', whiteSpace: 'pre-wrap', color: '#E8EDF5' }}
            >
              {Array.isArray(ds.rationale_lines) && ds.rationale_lines.length > 0
                ? ds.rationale_lines.join('\n')
                : legacyReasoning || 'No reasoning provided'}
            </div>
            {narrative && (
              <div className="mt-2">
                <small style={{ color: '#6A7D95' }}>Narrative</small>
                <div
                  className="p-3 rounded"
                  style={{ backgroundColor: '#101B2E', border: '1px solid #1E2D42', whiteSpace: 'pre-wrap', color: '#B8C8DC' }}
                >
                  {narrative}
                </div>
              </div>
            )}
          </Section>

          {/* Pattern influence (top-level) */}
          {patternInfluence && (
            <Section title="Pattern Influence">
              <div style={{ padding: 8, borderRadius: 4, background: C.bg2, border: `1px solid ${C.border}`, color: C.body, whiteSpace: 'pre-wrap', fontSize: 10, fontFamily: C.font }}>
                {patternInfluence}
              </div>
            </Section>
          )}

          {/* Pattern Tag Analysis */}
          {(ptag?.pattern ||
            (ptag?.tags && ptag.tags.length) ||
            ptag?.direction ||
            ptag?.vs_history ||
            ptag?.action_map_rule ||
            typeof ptag?.confidence === 'number') && (
            <Section title="Pattern Tag Analysis">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px 16px', marginBottom: 8 }}>
                {ptag.pattern          && <KeyVal label="Pattern"            value={ptag.pattern} />}
                {ptag.direction        && <KeyVal label="Direction"          value={ptag.direction} />}
                {typeof ptag.confidence === 'number' && <KeyVal label="Pattern Confidence" value={fmtPct(ptag.confidence)} />}
                {ptag.vs_history       && <KeyVal label="Vs History"         value={ptag.vs_history} />}
                {ptag.action_map_rule  && <KeyVal label="Rule"               value={ptag.action_map_rule} />}
              </div>
              {!!(ptag.tags && ptag.tags.length) && (
                <div>
                  <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Tags</div>
                  <div style={{ fontSize: 10, color: C.body }}>{ptag.tags.join(', ')}</div>
                </div>
              )}
            </Section>
          )}

          {/* Chart LLM Analysis */}
          {chartLLM && (
            <Section title="Chart LLM Analysis">
              <ChartLLMAnalysis data={chartLLM} />
            </Section>
          )}

          {/* Performance (Similar Cases) */}
          {(perf &&
            (perf.total_similar_cases ||
              perf.success_rate !== undefined ||
              perf.similarity_score !== undefined)) && (
            <Section title="Performance · Similar Cases">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px 16px', marginBottom: 8 }}>
                {perf.total_similar_cases !== undefined && <KeyVal label="Cases"          value={perf.total_similar_cases} />}
                {perf.success_rate        !== undefined && <KeyVal label="Success"        value={fmtPct(perf.success_rate)} />}
                {perf.target_hit_rate     !== undefined && <KeyVal label="Target Hit"     value={fmtPct(perf.target_hit_rate)} />}
                {perf.stop_hit_rate       !== undefined && <KeyVal label="Stop Hit"       value={fmtPct(perf.stop_hit_rate)} />}
                {perf.avg_pnl             !== undefined && <KeyVal label="Avg P&L"        value={fmtNum(perf.avg_pnl, 3)} />}
                {perf.expected_value      !== undefined && <KeyVal label="Expected Value" value={fmtNum(perf.expected_value, 3)} />}
                {perf.similarity_score    !== undefined && <KeyVal label="Similarity"     value={fmtPct(perf.similarity_score)} />}
                {perf.confidence_level    && <KeyVal label="Confidence Level" value={perf.confidence_level} />}
              </div>
              {(perf.supports_decision !== undefined || perf.used_weight) && (
                <div style={{ fontSize: 10, color: C.body }}>
                  Supports Decision:{' '}
                  <span style={{ color: C.text, fontWeight: 500 }}>{perf.supports_decision ? 'Yes' : 'No'}</span>
                  {perf.used_weight ? <> · Weight: <span style={{ color: C.text, fontWeight: 500 }}>{perf.used_weight}</span></> : null}
                </div>
              )}
            </Section>
          )}

          {/* Memory Summary */}
          {(msum?.found_cases ||
            msum?.recent_cases ||
            msum?.historical_cases ||
            msum?.top_similarity ||
            msum?.note) && (
            <Section title="Memory Summary">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px 16px', marginBottom: msum.note ? 8 : 0 }}>
                {msum.found_cases       !== undefined && <KeyVal label="Found"          value={msum.found_cases} />}
                {msum.recent_cases      !== undefined && <KeyVal label="Recent"         value={msum.recent_cases} />}
                {msum.historical_cases  !== undefined && <KeyVal label="Historical"     value={msum.historical_cases} />}
                {msum.top_similarity    !== undefined && <KeyVal label="Top Similarity" value={fmtPct(msum.top_similarity)} />}
              </div>
              {msum.note && (
                <div>
                  <div style={{ fontSize: 9, color: C.meta, marginBottom: 3 }}>Note</div>
                  <div style={{ fontSize: 10, color: C.body }}>{msum.note}</div>
                </div>
              )}
            </Section>
          )}

          {/* Thinking Snapshots */}
          {(think?.initial_thinking || think?.combined_thinking || think?.final_thinking) && (
            <Section title="Thinking Snapshots">
              {think.initial_thinking && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, color: C.meta, marginBottom: 6, letterSpacing: '0.06em' }}>INITIAL THINKING</div>
                  <div style={{ padding: 12, borderRadius: 5, backgroundColor: C.bg2, border: `1px solid ${C.border}` }}>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Scope & Decision</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Pill color={C.meta}>{think.initial_thinking.scope || 'unknown'}</Pill>
                        <Pill color={decisionColor(think.initial_thinking.provisional_decision)}>
                          {think.initial_thinking.provisional_decision || '—'}
                        </Pill>
                        {typeof think.initial_thinking.confidence === 'number' && (
                          <Pill color={C.cyan}>Conf: {think.initial_thinking.confidence.toFixed(2)}</Pill>
                        )}
                      </div>
                    </div>
                    {think.initial_thinking.initial_reasoning && (
                      <div>
                        <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Reasoning</div>
                        <div style={{ padding: 8, borderRadius: 4, backgroundColor: C.bg, border: `1px solid ${C.border}`, whiteSpace: 'pre-wrap', color: C.body, fontSize: 10, fontFamily: C.font }}>
                          {think.initial_thinking.initial_reasoning}
                        </div>
                      </div>
                    )}
                    {think.initial_thinking.rule_votes && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: 'pointer', color: C.meta, fontSize: 10 }}>Rule votes</summary>
                        <div style={{ marginTop: 6 }}><JsonBlock data={think.initial_thinking.rule_votes} /></div>
                      </details>
                    )}
                  </div>
                </div>
              )}

              {think.combined_thinking && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9, color: C.meta, marginBottom: 6, letterSpacing: '0.06em' }}>COMBINED THINKING</div>
                  <div style={{ padding: 12, borderRadius: 5, backgroundColor: C.bg2, border: `1px solid ${C.border}` }}>
                    {think.combined_thinking.consistency_recommendation && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Consistency Check</div>
                        <Pill color={
                          think.combined_thinking.consistency_recommendation === 'CONFIRM'  ? C.green :
                          think.combined_thinking.consistency_recommendation === 'OVERRIDE' ? C.amber : C.meta
                        }>
                          {think.combined_thinking.consistency_recommendation}
                        </Pill>
                      </div>
                    )}
                    {think.combined_thinking.consistency_reasoning && (
                      <div>
                        <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Integrated Analysis</div>
                        <div style={{ padding: 8, borderRadius: 4, backgroundColor: C.bg, border: `1px solid ${C.border}`, whiteSpace: 'pre-wrap', color: C.body, fontSize: 10, fontFamily: C.font }}>
                          {think.combined_thinking.consistency_reasoning}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {think.final_thinking && (
                <div style={{ marginBottom: 4 }}>
                  <div style={{ fontSize: 9, color: C.meta, marginBottom: 6, letterSpacing: '0.06em' }}>FINAL THINKING</div>
                  <div style={{ padding: 12, borderRadius: 5, backgroundColor: C.bg2, border: `1px solid ${C.border}` }}>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 9, color: C.meta, marginBottom: 5 }}>Decision & Confidence</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* THE FIX: always use decisionColor so PROCEED/SKIP/BUY are never black */}
                        <Pill color={decisionColor(think.final_thinking.decision)} size={11}>
                          {think.final_thinking.decision || '—'}
                        </Pill>
                        {think.final_thinking.confidence && (
                          <Pill color={C.meta}>{think.final_thinking.confidence}</Pill>
                        )}
                      </div>
                    </div>

                    {think.final_thinking.final_reasoning && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Final Reasoning</div>
                        <div style={{ padding: 8, borderRadius: 4, backgroundColor: C.bg, border: `1px solid ${C.border}`, whiteSpace: 'pre-wrap', color: C.body, fontSize: 10, fontFamily: C.font }}>
                          {think.final_thinking.final_reasoning}
                        </div>
                      </div>
                    )}

                    {think.final_thinking.trigger_plan && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 9, color: C.meta, marginBottom: 6 }}>TRIGGER PLAN</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <Pill color={C.meta}>
                            Next: {think.final_thinking.trigger_plan.next_check || '—'}
                          </Pill>
                          {think.final_thinking.trigger_plan.level_trigger && (
                            <Pill color={C.blue}>Level: {think.final_thinking.trigger_plan.level_trigger}</Pill>
                          )}
                          {typeof think.final_thinking.trigger_plan.false_break_ack === 'boolean' && (
                            <Pill color={think.final_thinking.trigger_plan.false_break_ack ? C.green : C.meta}>
                              False-break: {think.final_thinking.trigger_plan.false_break_ack ? 'Yes' : 'No'}
                            </Pill>
                          )}
                        </div>
                        {think.final_thinking.trigger_plan.note && (
                          <div style={{ marginTop: 6, fontSize: 10, color: C.meta }}>{think.final_thinking.trigger_plan.note}</div>
                        )}
                      </div>
                    )}

                    {(think.final_thinking.constraints_applied || think.final_thinking.audit_tags) && (
                      <details style={{ marginTop: 8 }}>
                        <summary style={{ cursor: 'pointer', color: C.meta, fontSize: 10 }}>Constraints & audit</summary>
                        <div style={{ marginTop: 6 }}>
                          {think.final_thinking.constraints_applied && (
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Constraints Applied</div>
                              <JsonBlock data={think.final_thinking.constraints_applied} />
                            </div>
                          )}
                          {think.final_thinking.audit_tags?.length > 0 && (
                            <div>
                              <div style={{ fontSize: 9, color: C.meta, marginBottom: 4 }}>Audit Tags</div>
                              <div style={{ fontSize: 10, color: C.body }}>{think.final_thinking.audit_tags.join(', ')}</div>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Risk, Contradictions & Ops */}
          {(rctl?.recommendation || contra?.model_vs_pattern || ops?.total_latency_s !== undefined) && (
            <Section title="Risk & Ops">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '8px 16px', marginBottom: 8 }}>
                {rctl?.recommendation    && <KeyVal label="Risk Recommendation" value={rctl.recommendation} />}
                {rctl?.vol_buffer_basis  && <KeyVal label="Vol Basis"           value={rctl.vol_buffer_basis} />}
                {contra?.model_vs_pattern && <KeyVal label="Model vs Pattern"   value={contra.model_vs_pattern} />}
                {contra?.note            && <KeyVal label="Note"                value={contra.note} color={C.body} />}
              </div>
              {(ops.llm_calls !== undefined || ops.total_latency_s !== undefined || ops.cache_hit_rate !== undefined) && (
                <div style={{ fontSize: 10, color: C.body }}>
                  Ops: LLM calls <span style={{ color: C.text, fontWeight: 500 }}>{ops.llm_calls ?? '—'}</span>
                  {' · '}Latency{' '}
                  <span style={{ color: C.text, fontWeight: 500 }}>
                    {typeof ops.total_latency_s === 'number' ? ops.total_latency_s.toFixed(2) : ops.total_latency_s} s
                  </span>
                  {' · '}Cache <span style={{ color: C.text, fontWeight: 500 }}>{ops.cache_hit_rate !== undefined ? fmtPct(ops.cache_hit_rate) : '—'}</span>
                </div>
              )}
            </Section>
          )}

          {/* Timeframe protection */}
          {isNonEmptyObject(timeframe) && (
            <Section title="Timeframe Protection">
              <JsonBlock data={timeframe} />
            </Section>
          )}

          {/* Raw GenAI Message - Collapsible */}
          {genAIraw && (
            <Section title="Raw Agent Story JSON">
              <details>
                <summary style={{
                  cursor: 'pointer', color: C.meta, padding: '6px 10px',
                  backgroundColor: C.bg2, border: `1px solid ${C.border}`,
                  borderRadius: 4, fontSize: 10, fontFamily: C.font,
                }}>
                  View complete agent story response
                </summary>
                <div style={{ marginTop: 8 }}>
                  <JsonBlock data={genAIraw} />
                </div>
              </details>
            </Section>
          )}

          {/* Legacy/experimental blocks */}
          <OptionalSection title="Exit Risk"  value={exitRisk} />
          <OptionalSection title="Red Flags"  value={redFlags} />
          <OptionalSection title="Journey"    value={journey} />
          <OptionalSection title="Signals"    value={signals} />

          {/* Unknown keys safety-net */}
          {(() => {
            if (!isNonEmptyObject(agentStory)) return null;
            const known = new Set([
              'schema_version',
              'ids',
              'decision_context',
              'decision',
              'performance',
              'memory_summary',
              'pattern_tag_analysis',
              'thinking_snapshots',
              'risk_controls',
              'contradictions',
              'ops',
              'timeframe_protection',
              'chart_analysis',
              'market_snapshot',
              'regime_summary',
            ]);
            const leftovers = Object.fromEntries(Object.entries(agentStory).filter(([k]) => !known.has(k)));
            if (!isNonEmptyObject(leftovers)) return null;
            return (
              <Section title="Additional Agent Fields">
                <JsonBlock data={leftovers} />
              </Section>
            );
          })()}
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: C.bg1, borderTop: `1px solid ${C.border}` }}>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            style={{ color: C.body, borderColor: C.border, fontFamily: C.font, fontSize: 11 }}
          >
            Close
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default EvaluationDetailModal;